/** SUNTRONIX ONE — Product Master (TECHNOLOGY → PRODUCT MASTER).
 *
 *  Single-file tab shell.  All seven panels live below.  Suntronix-
 *  internal only; the SuntronixOnly gate lives in App.js.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { VtigerTable } from "@/components/app/VtigerTable";
import { sourceLabel } from "@/lib/legacySource";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BoldableTextarea } from "@/components/commercial/BoldableTextarea";
import SmartFormatBulk from "@/components/technology/SmartFormatBulk";
import OemSpecFetch from "@/components/technology/OemSpecFetch";
import ProductEnrichPanel, { useAutoEnrich } from "@/components/products/ProductEnrichPanel";
import OemIntelligencePanel from "@/components/technology/OemIntelligencePanel";
import { EmphasisProfilesButton } from "@/components/technology/EmphasisProfiles";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Package, Boxes, Tag, Layers, BookOpen, Upload, Gauge,
  RefreshCw, Plus, Search, Copy, Power, PowerOff, History, Trash2,
  Download, Filter, AlertTriangle, CheckCircle2, XCircle,
  Sparkles, ArrowLeft, FileText, ShieldCheck, Lock, Pencil, Loader2, ChevronDown,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { UploadModal, DOC_TYPES as TECH_DOC_TYPES } from "@/pages/technology/TechLibrary";
import { AdminChip } from "@/components/app/AdminMode";
import ProductContentPanel from "@/components/product/ProductContentPanel";
import SmartAttachPanel from "@/components/orders/SmartAttachPanel";
import { MasterActions } from "@/components/app/MasterActions";
import { HsnPicker } from "@/components/app/HsnPicker";
import { ProductFormModal, Field } from "@/components/products/ProductFormModal";


const Chip = ({ active, onClick, testid, children }) => (
  <button onClick={onClick} data-testid={testid}
    className={"rounded-full text-[11.5px] font-medium px-2.5 py-1 border transition-colors " +
      (active ? "bg-slate-900 text-white border-slate-900"
              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")}>
    {children}
  </button>
);


const TABS = [
  { id: "overview",    label: "Overview",     icon: Gauge },
  { id: "products",   label: "Products",     icon: Boxes },
  { id: "brands",      label: "Brands",       icon: Tag },
  { id: "categories", label: "Categories",  icon: Layers },
  { id: "pricebook",  label: "Price Book",  icon: BookOpen },
  { id: "import",      label: "Import",       icon: Upload },
  { id: "quality",     label: "Data Quality", icon: Gauge },
];


export default function ProductMaster() {
  const [tab, setTab] = useState("overview");
  const [selected, setSelected] = useState(null);   // product id → 360 view
  const [searchParams] = useSearchParams();

  useEffect(() => { if (searchParams.get("pi") || searchParams.get("new") === "1") setTab("products"); }, [searchParams]);

  useEffect(() => {
    const pid = searchParams.get("product");
    if (pid) { setTab("products"); setSelected(pid); }
  }, [searchParams]);


  return (
    <div className="space-y-5 max-w-[1600px]" data-testid="product-master-page">
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-[320px]">
        <div className="text-[11px] uppercase tracking-[.16em] text-slate-500">
          Technology
        </div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-1 flex items-center gap-2">
          <Package className="h-6 w-6 text-emerald-700" /> Product Master
          <Badge className="text-[10px] ml-2 bg-emerald-50 border-emerald-200 text-emerald-800">
            Central catalogue
          </Badge>
          <AdminChip area="product" label="Configure product fields" testid="admin-chip-product" />
          <AdminChip area="picklists" pl="product_category" label="Edit categories" testid="admin-chip-product-cats" />
        </h1>
        <p className="text-xs text-slate-500 mt-1 max-w-3xl">
          The single source of truth for every product record used across
          Quotations, Vendor POs, Assets, Field Service and Warranty.
        </p>
        </div>
        
        <SmartFormatBulk />
        <EmphasisProfilesButton />
      </div>

      {/* Tab strip */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200"
        data-testid="tab-strip">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id}
              onClick={() => { setTab(t.id); setSelected(null); }}
              data-testid={`tab-${t.id}`}
              className={"px-3 py-2 text-[12px] font-medium flex items-center gap-1.5 border-b-2 -mb-px transition "
                + (active
                  ? "border-emerald-700 text-emerald-800 bg-emerald-50/40"
                  : "border-transparent text-slate-600 hover:text-slate-900")}>
              <Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {selected ? (
        <Product360 productId={selected} onBack={() => setSelected(null)} />
      ) : tab === "overview"   ? <OverviewPanel />
        : tab === "products"   ? <ProductsPanel onOpen={setSelected} />
        : tab === "brands"      ? <BrandsPanel />
        : tab === "categories" ? <CategoriesPanel />
        : tab === "pricebook"  ? <PriceBookPanel />
        : tab === "import"      ? <ImportPanel />
        : tab === "quality"     ? <DataQualityPanel />
        : null}
    </div>
  );
}


/* ─── Overview ─────────────────────────────────────────────────── */
function OverviewPanel() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/product-master/overview");
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="overview-panel">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Stat label="Total products"      value={data?.total_products ?? "—"} />
        <Stat label="Active"                 value={data?.active_products ?? "—"} tone="ok" />
        <Stat label="Inactive"               value={data?.inactive_products ?? "—"} tone="warn" />
        <Stat label="Brands"                 value={data?.brands ?? "—"} />
        <Stat label="Categories"           value={data?.categories ?? "—"} />
        <Stat label="Price-book entries" value={data?.pricebook_entries ?? "—"} />
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-emerald-700" />
          <div className="text-[13px] font-semibold text-slate-800">Recent activity</div>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded">
          <table className="min-w-full text-[11px]">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
              <tr>
                <th className="p-2 text-left">At</th>
                <th className="p-2 text-left">Op</th>
                <th className="p-2 text-left">Actor</th>
                <th className="p-2 text-left">Product</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent_activity || []).map((h) => (
                <tr key={h.id} className="border-t border-slate-100">
                  <td className="p-2 text-slate-500">{h.at}</td>
                  <td className="p-2 font-mono text-emerald-800">{h.op}</td>
                  <td className="p-2 text-slate-600">{h.actor}</td>
                  <td className="p-2 font-mono text-slate-500">{h.product_id}</td>
                </tr>
              ))}
              {!data?.recent_activity?.length && (
                <tr><td colSpan={4} className="p-6 text-center text-slate-400">
                  No activity yet — add a product from the Products tab.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


/* ─── Products list ────────────────────────────────────────────── */
function BulkActionsMenu({ selectedIds, onDone }) {
  const [admin, setAdmin] = useState(false);
  const [selOpen, setSelOpen] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const [enriching, setEnriching] = useState(false);
  useEffect(() => {
    api.get("/descriptions/smart-format/status").then(() => setAdmin(true)).catch(() => setAdmin(false));
  }, []);
  const hasSel = selectedIds.length > 0;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" data-testid="bulk-actions-menu">
            Bulk Actions <ChevronDown className="h-3 w-3 ml-1.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="text-[11px] text-slate-500">
            {hasSel ? `${selectedIds.length} product(s) selected` : "No products selected"}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={!hasSel || enriching} data-testid="bulk-enrich-selected"
            onSelect={async () => { setEnriching(true); try { const { data } = await api.post("/product-enrich/bulk", { product_ids: selectedIds }); toast.success(`Enrich Products: ${data.exact} exact · ${data.review} review required · ${data.not_found} no source · ${data.skipped} without part number`); onDone?.(); } catch (e) { toast.error(e.response?.data?.detail || "Bulk enrichment failed"); } finally { setEnriching(false); } }}>
            <Sparkles className="h-3.5 w-3.5 mr-2 text-emerald-600" /> {enriching ? "Enriching…" : "Enrich Products (Product Intelligence)"}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!hasSel} data-testid="bulk-smart-format-selected"
            onSelect={() => setTimeout(() => setSelOpen(true), 0)}>
            <Sparkles className="h-3.5 w-3.5 mr-2 text-indigo-600" /> Smart Format Selected
          </DropdownMenuItem>
          {admin && (
            <DropdownMenuItem data-testid="bulk-smart-format-all"
              onSelect={() => setTimeout(() => setAllOpen(true), 0)}>
              <Sparkles className="h-3.5 w-3.5 mr-2 text-indigo-600" /> Smart Format All
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <SmartFormatBulk headless presetIds={selectedIds} open={selOpen} onOpenChange={setSelOpen} onDone={onDone} />
      <SmartFormatBulk headless open={allOpen} onOpenChange={setAllOpen} onDone={onDone} />
    </>
  );
}

function ProductsPanel({ onOpen }) {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [q, setQ]              = useState("");
  const [brand, setBrand]     = useState("");
  const [category, setCat]  = useState("");
  const [status, setStatus] = useState("");
  const [hsnStatus, setHsnStatus] = useState("");
  const [hsnEdit, setHsnEdit] = useState(null);
  const [page, setPage]     = useState(1);
  const [busy, setBusy]     = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [duping, setDuping]      = useState(null);
  const [imgMap, setImgMap]   = useState({});
  const [piParams] = useSearchParams();
  useEffect(() => {
    const pi = piParams.get("pi"); // deep link from Apple CTO Master / Product Intelligence: open Add product with the part number ready to fetch
    if (pi) { setEditing(null); setDuping({ __prefill: true, oem_part_number: pi, brand: pi.toUpperCase().startsWith("Z") ? "Apple" : "", product_type: pi.toUpperCase().startsWith("Z") ? "CTO" : "", product_name: "" }); setShowForm(true); }
  }, [piParams]);
  useEffect(() => { if (piParams.get("new") === "1") { setEditing(null); setDuping(null); setShowForm(true); } }, [piParams]);  // top-bar "Add Product" one-click
  const [selectedIds, setSelectedIds] = useState([]);
  const [planCode, setPlanCode] = useState("");
  const [warrantyGroup, setWarrantyGroup] = useState("");
  const [warrantyMode, setWarrantyMode] = useState(false);
  const [hsnPanel, setHsnPanel] = useState(false);
  const [hsnInfo, setHsnInfo] = useState(null);
  const [hsnCode, setHsnCode] = useState("");
  const [hsnBusy, setHsnBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/product-master/products", {
        params: { q, brand, category, status, hsn_status: hsnStatus || undefined,
                  plan_code: planCode || undefined, warranty_group: warrantyGroup || undefined,
                  page, page_size: 50 }});
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, [q, brand, category, status, hsnStatus, planCode, warrantyGroup, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!items.length) { setImgMap({}); return; }
    api.post("/pc/image-map", { product_ids: items.map((p) => p.id) })
      .then(({ data }) => setImgMap(data.images || {})).catch(() => {});
  }, [items]);

  const setStatusFor = async (id, target) => {
    try {
      await api.post(`/product-master/products/${id}/${target}`);
      toast.success(`Product ${target}d`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteOne = async (p) => {
    if (!window.confirm(`Delete "${p.product_name}"?\n\nIt will be archived and removed from the catalogue. This can be restored by Suntronix if needed.`)) return;
    try {
      await api.delete(`/product-master/products/${p.id}`);
      toast.success("Product deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const bulkDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected product(s)?\n\nThey will be archived and removed from the catalogue.`)) return;
    try {
      const { data } = await api.post("/product-master/products/bulk-delete", { ids: selectedIds });
      toast.success(`${data.deleted} product(s) deleted${data.failed?.length ? ` · ${data.failed.length} failed` : ""}`);
      setSelectedIds([]);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
  };

  const doExport = async () => {
    try {
      const res = await api.get("/product-master/export",
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "product-master.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  // ---- Microsoft Warranty helpers (§22 filters, §12 Finance HSN sign-off)
  const WARRANTY_PLANS = [["EHS", "EHS"], ["EHS_PLUS", "EHS Plus"], ["CFB", "CFB"], ["CFB_PLUS", "CFB Plus"]];
  const WARRANTY_FAMILIES = [["pro_11_12", "Surface Pro 11 / 12"], ["pro_12_inch", "Surface Pro 12-inch"],
                             ["laptop_13", "Surface Laptop 13-inch"], ["laptop_7_8", "Surface Laptop 7 / 8"]];

  const enterWarranty = () => {
    setWarrantyMode(true); setBrand("Microsoft"); setCat("Warranty & Protection");
    setPlanCode(""); setWarrantyGroup(""); setQ(""); setPage(1);
  };
  const exitWarranty = () => {
    setWarrantyMode(false); setBrand(""); setCat(""); setPlanCode(""); setWarrantyGroup(""); setPage(1);
  };

  const openHsnPanel = async () => {
    setHsnPanel(true);
    try { const { data } = await api.get("/warranty-plans/hsn/status"); setHsnInfo(data); setHsnCode(data.current_hsn_sac || ""); }
    catch { setHsnInfo(null); }
  };
  const applyHsn = async () => {
    if (!hsnCode.trim()) { toast.error("Enter the HSN / SAC code"); return; }
    setHsnBusy(true);
    try {
      const { data } = await api.post("/warranty-plans/hsn/approve", { hsn_sac: hsnCode.trim() });
      toast.success(`HSN ${data.hsn_sac} signed off on ${data.updated} warranty SKUs`);
      setHsnPanel(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sign-off failed"); }
    finally { setHsnBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="products-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search name / code / SKU / OEM / model…"
            className="h-8 text-[12px] pl-7"
            value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
            data-testid="products-search" />
        </div>
        <Input placeholder="Brand" value={brand} className="h-8 text-[12px] w-32"
          onChange={(e) => { setPage(1); setBrand(e.target.value); }} />
        <Input placeholder="Category" value={category} className="h-8 text-[12px] w-36"
          onChange={(e) => { setPage(1); setCat(e.target.value); }} />
        <select className="h-8 text-[12px] border border-slate-200 rounded px-2"
          value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }}>
          <option value="">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
        </select>
        <select className="h-8 text-[12px] border border-slate-200 rounded px-2"
          value={hsnStatus} onChange={(e) => { setPage(1); setHsnStatus(e.target.value); }}
          data-testid="products-hsn-filter">
          <option value="">All HSN</option>
          <option value="VERIFIED">HSN Verified</option>
          <option value="SUGGESTED">HSN Suggested</option>
          <option value="REVIEW_REQUIRED">HSN Review required</option>
          <option value="MISSING">HSN Missing</option>
        </select>
        <Button onClick={load} disabled={busy} size="sm" variant="outline"
          data-testid="products-refresh">
          <RefreshCw className={"h-3 w-3 mr-1.5 " + (busy ? "animate-spin" : "")} />
          Refresh
        </Button>
        <Button onClick={doExport} size="sm" variant="outline"
          data-testid="products-export">
          <Download className="h-3 w-3 mr-1.5" /> Export
        </Button>
        <BulkActionsMenu selectedIds={selectedIds} onDone={() => { setSelectedIds([]); load(); }} />
        <Button onClick={() => { setEditing(null); setDuping(null); setShowForm(true); }}
          size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white"
          data-testid="products-add">
          <Plus className="h-3 w-3 mr-1.5" /> Add product
        </Button>
      </div>

      {/* Microsoft Warranty quick filters (§22) + Finance HSN sign-off (§12) */}
      <div className="flex flex-wrap items-center gap-1.5" data-testid="warranty-filter-chips">
        {!warrantyMode ? (
          <button onClick={enterWarranty} data-testid="warranty-chip-enter"
            className="inline-flex items-center gap-1.5 rounded-full border border-sky-300 bg-sky-50 text-sky-800 text-[11.5px] font-medium px-3 py-1 hover:bg-sky-100">
            <ShieldCheck className="h-3.5 w-3.5" /> Microsoft Warranty
          </button>
        ) : (
          <>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mr-1">Plan</span>
            <Chip active={!planCode} onClick={() => { setPlanCode(""); setPage(1); }} testid="warranty-chip-all">All</Chip>
            {WARRANTY_PLANS.map(([c, l]) => (
              <Chip key={c} active={planCode === c} onClick={() => { setPlanCode(c); setPage(1); }}
                testid={`warranty-chip-plan-${c}`}>{l}</Chip>
            ))}
            <span className="mx-1 h-4 w-px bg-slate-200" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mr-1">Device</span>
            <Chip active={!warrantyGroup} onClick={() => { setWarrantyGroup(""); setPage(1); }} testid="warranty-chip-fam-all">All</Chip>
            {WARRANTY_FAMILIES.map(([c, l]) => (
              <Chip key={c} active={warrantyGroup === c} onClick={() => { setWarrantyGroup(c); setPage(1); }}
                testid={`warranty-chip-fam-${c}`}>{l}</Chip>
            ))}
            <span className="mx-1 h-4 w-px bg-slate-200" />
            <button onClick={openHsnPanel} data-testid="warranty-hsn-signoff"
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-[11.5px] font-medium px-3 py-1 hover:bg-amber-100">
              <Lock className="h-3.5 w-3.5" /> HSN Sign-off
            </button>
            <button onClick={exitWarranty} data-testid="warranty-chip-exit"
              className="text-[11px] text-slate-500 hover:text-slate-700 ml-1">Clear</button>
          </>
        )}
      </div>

      {hsnPanel && (
        <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3" data-testid="warranty-hsn-panel">
          <div className="flex items-center gap-2 mb-2">
            <Lock className="h-4 w-4 text-amber-700" />
            <span className="text-[13px] font-semibold text-amber-900">Warranty HSN / SAC sign-off (Finance)</span>
          </div>
          {hsnInfo && (
            <div className="text-[12px] text-slate-600 mb-2">
              {hsnInfo.verified}/{hsnInfo.total} verified · {hsnInfo.pending} awaiting sign-off · GST {hsnInfo.gst_pct}%
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Input placeholder="HSN / SAC code (e.g. 998719)" value={hsnCode}
              onChange={(e) => setHsnCode(e.target.value)} className="h-8 text-[12px] w-52"
              data-testid="warranty-hsn-input" />
            <Button onClick={applyHsn} disabled={hsnBusy} size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white h-8" data-testid="warranty-hsn-apply">
              {hsnBusy ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1.5" />}
              Approve & clear review on all 48
            </Button>
            <button onClick={() => setHsnPanel(false)} className="text-[11px] text-slate-500 hover:text-slate-700">Cancel</button>
          </div>
          <div className="text-[11px] text-slate-500 mt-2">Applies one approved HSN/SAC to every Microsoft warranty SKU and sets tax status to VERIFIED. GST stays 18%.</div>
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2"
          data-testid="products-bulk-bar">
          <span className="text-[12px] font-semibold text-rose-800">{selectedIds.length} selected</span>
          <Button onClick={bulkDelete} size="sm"
            className="bg-rose-600 hover:bg-rose-700 text-white h-7"
            data-testid="products-bulk-delete">
            <Trash2 className="h-3 w-3 mr-1.5" /> Delete selected
          </Button>
          <span className="text-[11px] text-slate-500">Use <b>Bulk Actions ▾</b> above for Smart Format</span>
          <button onClick={() => setSelectedIds([])}
            className="text-[11px] text-slate-500 hover:text-slate-700">Clear</button>
        </div>
      )}

      <VtigerTable testid="products-table" rows={items} pageSize={20}
        viewKey="products" onCheckedChange={setSelectedIds}
        filterNote="Column filters apply to loaded rows — use the search above for all products"
        onRowClick={(p) => onOpen(p.id)}
        columns={[
          { key: "product_name", label: "Product Name",
            render: (p) => (
              <span className="inline-flex items-center gap-2">
                <img src={imgMap[p.id] || `/api/product-images/placeholder/${p.id}.svg`} alt="" loading="lazy"
                  className="h-7 w-7 object-contain rounded border border-slate-100 bg-white shrink-0"
                  data-testid={`product-thumb-${p.id}`} />
                <span className="font-semibold text-sky-700">{p.product_name}</span>
              </span>) },
          { key: "hsn_sac", label: "HSN Code", mono: true, width: 150,
            render: (p) => {
              const eff = p.hsn_status_effective || (p.hsn_sac ? "VERIFIED" : "MISSING");
              if (!p.hsn_sac || eff === "MISSING") {
                return (
                  <button onClick={(e) => { e.stopPropagation(); setHsnEdit(p); }}
                    data-testid={`hsn-required-${p.id}`}
                    className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 hover:underline whitespace-nowrap">
                    <AlertTriangle className="h-3 w-3" /> HSN REQUIRED
                  </button>);
              }
              const tone = eff === "VERIFIED" ? "text-slate-700"
                : eff === "SUGGESTED" ? "text-sky-700" : "text-amber-700";
              return (
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <span className={`font-mono ${tone}`} data-testid={`hsn-value-${p.id}`}>{p.hsn_sac}</span>
                  {eff !== "VERIFIED" && (
                    <span className="text-[8px] px-1 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">{eff.replace("_", " ")}</span>
                  )}
                  <button onClick={() => setHsnEdit(p)} data-testid={`hsn-edit-${p.id}`}
                    className="text-slate-300 hover:text-emerald-700"><Pencil className="h-3 w-3" /></button>
                </span>);
            } },
          { key: "oem_part_number", label: "Part Number", mono: true, width: 130 },
          { key: "brand", label: "Brand", width: 110 },
          { key: "category", label: "Category", width: 120 },
          { key: "model", label: "Model", width: 110 },
          { key: "gst_percent", label: "GST %", align: "right", num: true, width: 70 },
          { key: "standard_selling_price", label: "Sell (INR)", align: "right", num: true, width: 100 },
          { key: "status", label: "Status", width: 90,
            render: (p) => p.status === "ACTIVE"
              ? <Badge className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-800">Active</Badge>
              : <Badge className="text-[10px] bg-slate-100 border-slate-200 text-slate-600">Inactive</Badge> },
          { key: "_act", label: "Actions", filter: false, width: 130,
            render: (p) => (
              <span className="space-x-1.5" onClick={(e) => e.stopPropagation()}>
                <button className="text-[10px] text-slate-600 hover:text-emerald-700"
                  onClick={() => { setEditing(p); setDuping(null); setShowForm(true); }}
                  data-testid={`prod-edit-${p.id}`}>Edit</button>
                <button className="text-[10px] text-slate-600 hover:text-emerald-700"
                  onClick={() => { setDuping(p); setEditing(null); setShowForm(true); }}
                  data-testid={`prod-dup-${p.id}`}><Copy className="h-3 w-3 inline" /></button>
                {p.status === "ACTIVE" ? (
                  <button className="text-[10px] text-slate-600 hover:text-amber-700"
                    onClick={() => setStatusFor(p.id, "deactivate")}
                    data-testid={`prod-deact-${p.id}`}><PowerOff className="h-3 w-3 inline" /></button>
                ) : (
                  <button className="text-[10px] text-slate-600 hover:text-emerald-700"
                    onClick={() => setStatusFor(p.id, "activate")}
                    data-testid={`prod-act-${p.id}`}><Power className="h-3 w-3 inline" /></button>
                )}
                <button className="text-[10px] text-slate-600 hover:text-rose-700"
                  onClick={() => deleteOne(p)} title="Delete product"
                  data-testid={`prod-delete-${p.id}`}><Trash2 className="h-3 w-3 inline" /></button>
              </span>) },
        ]} />
      <div className="flex items-center justify-between text-[11px] text-slate-500 px-1">
        <span>Server page {page} · loaded {items.length} of {total.toLocaleString()} total</span>
        <span className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}>Prev 50</Button>
          <Button size="sm" variant="outline" disabled={items.length < 50}
            onClick={() => setPage((p) => p + 1)}>Next 50</Button>
        </span>
      </div>

      {showForm && (
        <ProductFormModal
          initial={editing || (duping?.__prefill ? { ...duping, __prefill: undefined } : duping ? { ...duping,
            product_code: "", sku: "", oem_part_number: "",
            product_name: `${duping.product_name} (Copy)` } : null)}
          mode={editing ? "edit" : duping && !duping.__prefill ? "duplicate" : "create"}
          sourceId={duping?.id}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
          onViewExisting={(id) => { setShowForm(false); onOpen(id); }}
        />
      )}
      {hsnEdit && (
        <HsnEditModal product={hsnEdit}
          onClose={() => setHsnEdit(null)}
          onSaved={() => { setHsnEdit(null); load(); }} />
      )}
    </div>
  );
}

/* ─── HSN assign / verify / change (grid + Product 360) ───────────
   Implements the "AI set once → reuse until manually changed" flow:
   AI suggest → Accept & Save (persists code + VERIFIED + who/when + AI
   basis/confidence), or a manual change that requires a reason. */
function HsnEditModal({ product, onClose, onSaved }) {
  const wasVerified = (product.hsn_status_effective || (product.hsn_sac ? "VERIFIED" : "MISSING")) === "VERIFIED"
    && !!(product.hsn_sac || "").trim();
  const wasSettled = ["VERIFIED", "REVIEW_REQUIRED"].includes(
    product.hsn_status_effective || (product.hsn_sac ? "VERIFIED" : "MISSING")) && !!(product.hsn_sac || "").trim();
  const [hsn, setHsn] = useState(product.hsn_sac || "");
  const [status, setStatus] = useState(product.hsn_status_effective
    || (product.hsn_sac ? "VERIFIED" : "MISSING"));
  const [gst, setGst] = useState(product.gst_percent ?? "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // AI suggestion state.
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);   // {code, description, confidence, reason}
  const [meta, setMeta] = useState({ confidence: null, ai_source: null, basis: null });

  const isSvc = (product.product_type || "").toUpperCase() === "SERVICE";
  const codeChanged = (product.hsn_sac || "").trim() !== hsn.trim();
  const needReason = wasSettled && codeChanged;

  const runSuggest = async () => {
    setSuggesting(true); setSuggestion(null);
    try {
      const { data } = await api.post("/master/hsn/suggest", {
        name: product.product_name, description: product.description || "",
        type: isSvc ? "SAC" : "HSN",
      });
      if (data.ai_suggestion) setSuggestion(data.ai_suggestion);
      else toast.info("ONE AI couldn't find a confident match — search or enter manually.");
    } catch { toast.error("AI suggestion failed"); }
    finally { setSuggesting(false); }
  };

  const acceptSuggestion = () => {
    if (!suggestion) return;
    setHsn(suggestion.code || suggestion.code_display || "");
    setStatus("VERIFIED");
    setMeta({
      confidence: suggestion.confidence ?? null,
      ai_source: "ONE AI",
      basis: suggestion.reason || suggestion.description || null,
    });
  };

  const save = async () => {
    if (needReason && !reason.trim()) { toast.error("A reason is required to change a verified HSN."); return; }
    setBusy(true);
    try {
      await api.patch(`/product-master/products/${product.id}/hsn`, {
        hsn_sac: hsn.trim(),
        hsn_status: hsn.trim() ? (status === "MISSING" ? "VERIFIED" : status) : "MISSING",
        gst_percent: gst === "" ? undefined : Number(gst),
        reason: reason.trim() || undefined,
        confidence: meta.confidence,
        ai_source: meta.ai_source,
        basis: meta.basis,
      });
      toast.success(hsn.trim() ? "HSN saved & verified ✓" : "HSN cleared");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-start justify-center overflow-y-auto p-4"
      data-testid="hsn-edit-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-2xl w-full max-w-md mt-16">
        <div className="p-4 border-b border-slate-200">
          <div className="text-sm font-semibold">{wasVerified ? "Change HSN" : "Assign HSN"} — {product.product_name}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">SKU/OEM: {product.oem_part_number || product.sku || "—"} · {product.category || "—"}</div>
        </div>
        <div className="p-4 space-y-3">
          {/* AI suggestion */}
          {!wasVerified && (
            <div>
              <button onClick={runSuggest} disabled={suggesting} data-testid="hsn-ai-suggest"
                className="w-full h-9 rounded-md border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 flex items-center justify-center gap-1.5 text-[12px] font-medium disabled:opacity-50">
                {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {suggesting ? "ONE AI is analysing…" : "Suggest with ONE AI"}
              </button>
              {suggestion && (
                <div className="mt-2 rounded-md border border-violet-200 bg-violet-50/60 p-2.5" data-testid="hsn-ai-suggestion">
                  <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-violet-700">
                    <Sparkles className="h-3 w-3" /> AI suggestion
                    {suggestion.confidence != null && <span className="ml-auto text-violet-600">{suggestion.confidence}% confidence</span>}
                  </div>
                  <div className="font-mono text-[14px] font-semibold text-slate-900 mt-0.5">{suggestion.code_display || suggestion.code}</div>
                  <div className="text-[11px] text-slate-600 leading-snug">{suggestion.description}</div>
                  {suggestion.reason && <div className="text-[10px] text-slate-500 italic mt-0.5">Basis: {suggestion.reason}</div>}
                  <button onClick={acceptSuggestion} data-testid="hsn-ai-accept"
                    className="mt-1.5 w-full h-7 rounded bg-violet-700 hover:bg-violet-800 text-white text-[11px] font-medium flex items-center justify-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Accept &amp; use this code
                  </button>
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-[11px]">HSN Code</Label>
            <Input value={hsn} onChange={(e) => { setHsn(e.target.value); setMeta({ confidence: null, ai_source: "Manual", basis: null }); }}
              placeholder="e.g. 84713010" className="font-mono" data-testid="hsn-edit-code" />
            <div className="text-[10px] text-slate-400 mt-1">Do not fabricate — leave blank to mark HSN REQUIRED.</div>
          </div>
          {meta.ai_source === "ONE AI" && (
            <div className="text-[10px] text-violet-700 -mt-1" data-testid="hsn-edit-ai-note">Will be saved as VERIFIED with ONE AI basis ({meta.confidence != null ? `${meta.confidence}%` : "—"}).</div>
          )}
          <div>
            <Label className="text-[11px]">Verification status</Label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}
              className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px]" data-testid="hsn-edit-status">
              <option value="VERIFIED">Verified</option>
              <option value="SUGGESTED">Suggested</option>
              <option value="REVIEW_REQUIRED">Review required</option>
              <option value="MISSING">Missing</option>
            </select>
          </div>
          <div>
            <Label className="text-[11px]">GST %</Label>
            <Input value={gst} onChange={(e) => setGst(e.target.value)} type="number"
              placeholder="e.g. 18" data-testid="hsn-edit-gst" />
          </div>
          {needReason && (
            <div data-testid="hsn-edit-reason-wrap">
              <Label className="text-[11px] text-amber-700">Reason for change (required)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this HSN being changed?" data-testid="hsn-edit-reason" />
              <div className="text-[10px] text-slate-400 mt-1">Old HSN <span className="font-mono">{product.hsn_sac}</span> will be kept in the change audit; issued documents keep their original HSN.</div>
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy}
            className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="hsn-edit-save">
            {busy ? "Saving…" : "Accept & Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}


/* ─── HSN panel for Product 360 (spec §23) ────────────────────── */
function HsnPanel({ p, onChange }) {
  const eff = p.hsn_status_effective || (p.hsn_sac ? "VERIFIED" : "MISSING");
  const tone = {
    VERIFIED: "bg-emerald-50 border-emerald-200 text-emerald-800",
    SUGGESTED: "bg-sky-50 border-sky-200 text-sky-800",
    REVIEW_REQUIRED: "bg-amber-50 border-amber-200 text-amber-800",
    MISSING: "bg-rose-50 border-rose-200 text-rose-700",
  }[eff] || "bg-slate-100 border-slate-200 text-slate-600";
  const dt = (v) => (v ? String(v).slice(0, 10) : null);
  return (
    <div className="mb-2" data-testid="p360-hsn-panel">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-slate-500">HSN / SAC</span>
        <button onClick={onChange} data-testid="p360-change-hsn"
          className="text-[11px] font-medium text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1">
          <Pencil className="h-3 w-3" />{eff === "MISSING" ? "Assign HSN" : "Change HSN"}
        </button>
      </div>
      <div className="flex items-center gap-2 mt-0.5">
        <span className="font-mono text-[15px] font-semibold text-slate-900" data-testid="p360-hsn-code">
          {p.hsn_sac || "HSN REQUIRED"}
        </span>
        <Badge className={tone + " text-[10px]"} data-testid="p360-hsn-status">{eff.replace("_", " ")}</Badge>
      </div>
      <div className="mt-1 space-y-0.5 text-[10px] text-slate-500" data-testid="p360-hsn-meta">
        {p.hsn_verified_by && <div>Verified by <b className="text-slate-700">{p.hsn_verified_by}</b>{dt(p.hsn_verified_at) ? ` · ${dt(p.hsn_verified_at)}` : ""}</div>}
        {p.hsn_ai_source && <div>Source: {p.hsn_ai_source}{p.hsn_ai_confidence != null ? ` · ${p.hsn_ai_confidence}% confidence` : ""}</div>}
        {p.hsn_basis && <div className="italic">Basis: {p.hsn_basis}</div>}
        {p.hsn_last_changed_at && <div>Last changed {dt(p.hsn_last_changed_at)}{p.hsn_last_changed_by ? ` by ${p.hsn_last_changed_by}` : ""}{p.hsn_change_reason ? ` — ${p.hsn_change_reason}` : ""}</div>}
        {eff === "REVIEW_REQUIRED" && p.hsn_review_reason && <div className="text-amber-700">{p.hsn_review_reason}</div>}
      </div>
    </div>
  );
}


/* ─── Product form modal: shared → components/products/ProductFormModal.jsx ─── */

/* ─── Product 360 view ─────────────────────────────────────────── */
function QuoteAppearanceDialog({ productId, onClose }) {
  const [q, setQ] = useState(null);
  useEffect(() => { api.get(`/product-enrich/quote-preview/${productId}`).then(({ data }) => setQ(data)).catch((e) => toast.error(e.response?.data?.detail || "Preview failed")); }, [productId]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="quote-appearance">
        <DialogHeader><DialogTitle className="text-[14px]">Quote appearance — exactly as the quotation renders it</DialogTitle></DialogHeader>
        {!q ? <div className="text-[12px] text-slate-500">Loading…</div> : (
          <div className="border border-slate-300 rounded-sm text-[11px] font-sans">
            <div className="grid grid-cols-[36%_9%_14%_6%_8%_12%_15%] bg-slate-100 font-bold border-b border-slate-300">{["Item Description", "HSN CODE", "Price Per Unit (INR)", "Qty", "GST", "GST Amount", "Total Price Incl. Of Taxes (INR)"].map((h) => <div key={h} className="px-1.5 py-1 border-r last:border-r-0 border-slate-300">{h}</div>)}</div>
            <div className="grid grid-cols-[36%_9%_14%_6%_8%_12%_15%] border-b border-slate-200">
              <div className="px-1.5 py-1.5 flex gap-2 min-w-0">{q.image && <img src={q.image} alt="" className="h-[60px] w-[60px] shrink-0 object-contain bg-white" data-testid="qa-image" />}<div className="min-w-0 flex-1"><div className="font-bold leading-snug" data-testid="qa-title">{q.title}</div><div className="text-[10.5px] leading-snug text-slate-700" data-testid="qa-headline" dangerouslySetInnerHTML={{ __html: String(q.headline || "").replace(/\*\*(.+?)\*\*/g, "<b>$1</b>") }} /></div></div>
              {[q.hsn || "—", q.price != null ? Number(q.price).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—", "1", q.gst_percent != null ? `${q.gst_percent}%` : "—", "—", "—"].map((v, i) => <div key={i} className="px-1.5 py-1.5 font-bold text-right border-l border-slate-200">{v}</div>)}
            </div>
            <div className="px-1.5 py-2 text-[10.5px] text-slate-600 leading-relaxed" data-testid="qa-description" dangerouslySetInnerHTML={{ __html: String(q.description || "").replace(/\*\*(.+?)\*\*/g, "<b>$1</b>") }} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Product360({ productId, onBack }) {
  const [data, setData] = useState(null);
  const [piOpen, setPiOpen] = useState(false);
  const [quotePrev, setQuotePrev] = useState(false);
  const [editing, setEditing] = useState(false);
  const [hsnEdit, setHsnEdit] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/product-master/products/${productId}`);
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, [productId]);
  useEffect(() => { load(); }, [load]);
  const p = data?.product;
  return (
    <div className="space-y-4" data-testid="product-360">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={onBack} data-testid="p360-back">
          <ArrowLeft className="h-3 w-3 mr-1" /> Back to products
        </Button>
        <Button size="sm" variant="outline" onClick={() => setQuotePrev(true)} data-testid="p360-quote-preview" className="border-indigo-200 text-indigo-700">Preview quote appearance</Button>
        <Button size="sm" variant="outline" onClick={() => setPiOpen(true)} data-testid="p360-pi-open" className="border-indigo-200 text-indigo-700"><Sparkles className="h-3 w-3 mr-1" /> Fetch Product Intelligence</Button>
        {p?.pi_status && <span className="text-[11px] rounded-full bg-slate-100 px-2 py-0.5" data-testid="p360-pi-status">PI: {String(p.pi_status).replace(/_/g, " ")}{p.pi_last_verified ? ` · verified ${new Date(p.pi_last_verified).toLocaleDateString("en-IN")}` : ""}</span>}
      </div>
      {p && <ProductEnrichPanel open={piOpen} onOpenChange={setPiOpen} partNumber={p.oem_part_number || p.sku || ""} brand={p.brand} productId={p.id} mode="apply" onApplied={load} />}
      {quotePrev && <QuoteAppearanceDialog productId={productId} onClose={() => setQuotePrev(false)} />}
      {!p ? (
        <div className="text-slate-500 text-[12px]">Loading…</div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Product 360</div>
                <div className="text-lg font-heading font-semibold text-slate-900">{p.product_name}</div>
                <div className="text-[11px] text-slate-500 font-mono">
                  {p.product_code || "—"} · {p.brand || "—"} · {p.model || "—"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {p.status === "ACTIVE"
                  ? <Badge className="bg-emerald-50 border-emerald-200 text-emerald-800">Active</Badge>
                  : <Badge className="bg-slate-100 border-slate-200 text-slate-600">Inactive</Badge>}
                <MasterActions type="product" id={productId} name={p.product_name}
                  active={p.status === "ACTIVE"} testid="product-360"
                  onEdit={() => setEditing(true)}
                  onChanged={(res) => { if (res?.deleted) onBack(); else load(); }} />
              </div>
            </div>
          </div>
          {editing && (
            <ProductFormModal initial={p} mode="edit"
              onClose={() => setEditing(false)}
              onSaved={() => { setEditing(false); load(); }} />
          )}
          {hsnEdit && (
            <HsnEditModal product={p}
              onClose={() => setHsnEdit(false)}
              onSaved={() => { setHsnEdit(false); load(); }} />
          )}

          <ProductContentPanel productId={productId} />

          {p.oem_spec && <OemIntelligencePanel product={p} onChanged={load} />}

          <SmartAttachPanel mode="product" productName={p.product_name} brand={p.brand} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Section360 title="Product Information">
              <Kv k="SKU"                 v={p.sku} />
              <Kv k="OEM Part Number" v={p.oem_part_number} />
              <Kv k="Category"           v={p.category} />
              <Kv k="Subcategory"      v={p.subcategory} />
              <Kv k="Product Type"    v={p.product_type} />
              <Kv k="Warranty"          v={p.warranty} />
              <Kv k="Country Avail."   v={(p.country_availability || []).join(", ")} />
              <Kv k="Description"      v={p.description} pre />
            </Section360>
            <Section360 title="Commercial Information">
              <HsnPanel p={p} onChange={() => setHsnEdit(true)} />
              <Kv k="GST %"     v={p.gst_percent} />
              <Kv k="Currency"  v={p.currency} />
            </Section360>
            <Section360 title="Pricing">
              <Kv k="Base Purchase Price"   v={p.base_purchase_price} />
              <Kv k="Standard Selling Price" v={p.standard_selling_price} />
              <Kv k="Selling Price (USD)"    v={p.selling_price_usd} />
            </Section360>
            <Section360 title="Availability">
              <Kv k="Status"                v={p.status} />
              <Kv k="Qty in Stock"        v={p.qty_in_stock} />
              <Kv k="Reorder Level"      v={p.reorder_level} />
              <Kv k="Country Availability" v={(p.country_availability || []).join(", ")} />
            </Section360>
            <Section360 title={`Related Quotations (${(data?.related_quotes || []).length})`}>
              {(data?.related_quotes || []).map((q) => (
                <div key={q.id} className="text-[11px] py-1 border-b border-slate-100 last:border-0">
                  <span className="font-mono">{q.code}</span> · {q.account_name} · {q.status}
                </div>
              ))}
              {!(data?.related_quotes || []).length && <div className="text-[11px] text-slate-400">None</div>}
            </Section360>
            <Section360 title={`Related Orders (${(data?.related_orders || []).length})`}>
              {(data?.related_orders || []).map((o) => (
                <div key={o.id} className="text-[11px] py-1 border-b border-slate-100 last:border-0">
                  <span className="font-mono">{o.code}</span> · {o.account_name} · {o.status}
                </div>
              ))}
              {!(data?.related_orders || []).length && <div className="text-[11px] text-slate-400">None</div>}
            </Section360>
            <Section360 title={`Related Assets (${(data?.related_assets || []).length})`}>
              {(data?.related_assets || []).map((a) => (
                <div key={a.id} className="text-[11px] py-1 border-b border-slate-100 last:border-0">
                  <span className="font-mono">{a.asset_tag || a.serial_number}</span> · {a.status}
                </div>
              ))}
              {!(data?.related_assets || []).length && <div className="text-[11px] text-slate-400">None</div>}
            </Section360>
            <Section360 title="System & Integration">
              <Kv k="Internal Product ID"     v={p.product_code} mono />
              <Kv k="Record ID"                v={p.id} mono />
              <Kv k="Legacy Model"            v={p.model} />
              <Kv k="Source"                     v={sourceLabel(p.source)} />
              <Kv k="Legacy import ID" v={p.external_crm_product_id} mono />
              <Kv k="Last Synced"             v={(p.last_synced_at || "").slice(0, 16).replace("T", " ") || "never"} />
              <Kv k="Created"                    v={`${p.created_at} · ${p.created_by}`} />
              <Kv k="Modified"                  v={`${p.updated_at} · ${p.updated_by}`} />
            </Section360>
            <Section360 title={`Change History (${(data?.history || []).length})`}>
              {(data?.history || []).slice(0, 15).map((h) => (
                <div key={h.id} className="text-[11px] py-1 border-b border-slate-100 last:border-0">
                  <span className="font-mono text-emerald-700">{h.op}</span> · {h.at} · {h.actor}
                </div>
              ))}
              {!(data?.history || []).length && <div className="text-[11px] text-slate-400">None</div>}
            </Section360>
          </div>

          {/* Tech Library — documents attached to this product */}
          <ProductContent product={p} />
          <ProductTechDocs product={p} />
        </>
      )}
    </div>
  );
}


function Section360({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}


function Kv({ k, v, pre, mono }) {
  return (
    <div className="text-[11px] grid grid-cols-3 gap-1 py-1 border-b border-slate-50 last:border-0">
      <div className="text-slate-500">{k}</div>
      <div className={"col-span-2 " + (pre ? "whitespace-pre-wrap " : "") + (mono ? "font-mono " : "") + "text-slate-800 break-all"}>
        {v == null || v === "" ? "—" : String(v)}
      </div>
    </div>
  );
}


/* ─── Brands ───────────────────────────────────────────────────── */
function BrandsPanel() {
  const [rows, setRows] = useState([]);
  const [n, setN] = useState({ name: "", country: "", description: "", active: true });
  const load = useCallback(async () => {
    try { const { data } = await api.get("/product-master/brands"); setRows(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!n.name.trim()) return;
    try { await api.post("/product-master/brands", n);
      toast.success("Brand added"); setN({ name: "", country: "", description: "", active: true });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="brands-panel">
      <div className="flex flex-wrap items-end gap-2 bg-white border border-slate-200 rounded-lg p-3">
        <Field label="Brand name *"><Input value={n.name} onChange={(e) => setN({...n, name: e.target.value})} data-testid="brand-name" /></Field>
        <Field label="Country"><Input value={n.country} onChange={(e) => setN({...n, country: e.target.value})} /></Field>
        <div className="flex-1"><Field label="Description"><Input value={n.description} onChange={(e) => setN({...n, description: e.target.value})} /></Field></div>
        <Button onClick={add} disabled={!n.name.trim()} className="bg-emerald-700 hover:bg-emerald-800 text-white h-9" data-testid="brand-add">
          <Plus className="h-3 w-3 mr-1" /> Add brand
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="min-w-full text-[11px]" data-testid="brands-table">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
            <tr>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Country</th>
              <th className="p-2 text-left">Active</th>
              <th className="p-2 text-left">Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(b => (
              <tr key={b.id} className="border-t border-slate-100">
                <td className="p-2 font-semibold text-slate-900">{b.name}</td>
                <td className="p-2 text-slate-700">{b.country || "—"}</td>
                <td className="p-2">{b.active ? <Badge className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-800">Yes</Badge> : <Badge className="text-[10px] bg-slate-100 border-slate-200 text-slate-600">No</Badge>}</td>
                <td className="p-2 text-slate-500 text-[10px]">{b.created_at}</td>
              </tr>
            ))}
            {!rows.length && (<tr><td colSpan={4} className="p-6 text-center text-slate-400">No brands yet.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ─── Categories ───────────────────────────────────────────────── */
function CategoriesPanel() {
  const [rows, setRows] = useState([]);
  const [n, setN] = useState({ name: "", parent_id: "", description: "", active: true });
  const load = useCallback(async () => {
    try { const { data } = await api.get("/product-master/categories"); setRows(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!n.name.trim()) return;
    try {
      await api.post("/product-master/categories",
        { ...n, parent_id: n.parent_id || null });
      toast.success("Category added");
      setN({ name: "", parent_id: "", description: "", active: true });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="categories-panel">
      <div className="flex flex-wrap items-end gap-2 bg-white border border-slate-200 rounded-lg p-3">
        <Field label="Category name *"><Input value={n.name} onChange={(e) => setN({...n, name: e.target.value})} data-testid="cat-name" /></Field>
        <Field label="Parent (optional)">
          <select className="h-9 border border-slate-200 rounded px-2 text-[13px]"
            value={n.parent_id} onChange={(e) => setN({...n, parent_id: e.target.value})}>
            <option value="">— none —</option>
            {rows.filter(c => !c.parent_id).map(c =>
              <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <div className="flex-1"><Field label="Description"><Input value={n.description} onChange={(e) => setN({...n, description: e.target.value})} /></Field></div>
        <Button onClick={add} disabled={!n.name.trim()} className="bg-emerald-700 hover:bg-emerald-800 text-white h-9" data-testid="cat-add">
          <Plus className="h-3 w-3 mr-1" /> Add category
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="min-w-full text-[11px]" data-testid="categories-table">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
            <tr>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Parent</th>
              <th className="p-2 text-left">Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(c => {
              const parent = rows.find(x => x.id === c.parent_id);
              return (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="p-2 font-semibold text-slate-900">{c.name}</td>
                  <td className="p-2 text-slate-700">{parent?.name || "—"}</td>
                  <td className="p-2">{c.active ? <Badge className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-800">Yes</Badge> : <Badge className="text-[10px] bg-slate-100 border-slate-200 text-slate-600">No</Badge>}</td>
                </tr>);})}
            {!rows.length && (<tr><td colSpan={3} className="p-6 text-center text-slate-400">No categories yet.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ─── Price Book ───────────────────────────────────────────────── */
function PriceBookPanel() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/product-master/pricebook"); setRows(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="pricebook-panel">
      <div className="text-[13px] font-semibold text-slate-800 mb-3 flex items-center gap-2">
        <BookOpen className="h-4 w-4 text-emerald-700" /> Price Book entries
        <Badge className="text-[10px] bg-slate-100 border-slate-200 text-slate-700">{rows.length}</Badge>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded">
        <table className="min-w-full text-[11px]" data-testid="pricebook-table">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
            <tr>
              <th className="p-2 text-left">Price Book</th>
              <th className="p-2 text-left">Product ID</th>
              <th className="p-2 text-right">Unit Price</th>
              <th className="p-2 text-left">Currency</th>
              <th className="p-2 text-left">Valid From</th>
              <th className="p-2 text-left">Valid To</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="p-2 text-slate-800">{r.price_book}</td>
                <td className="p-2 font-mono text-slate-500">{r.product_id}</td>
                <td className="p-2 text-right font-mono">{r.unit_price}</td>
                <td className="p-2 text-slate-700">{r.currency}</td>
                <td className="p-2 text-slate-500">{r.valid_from || "—"}</td>
                <td className="p-2 text-slate-500">{r.valid_to || "—"}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-400">
                No price-book entries yet.  Attach unit prices to products via <code>POST /api/product-master/pricebook</code>.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ─── Import ───────────────────────────────────────────────────── */
function ImportPanel() {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async (kind = "xlsx") => {
    try {
      const path = kind === "xlsx"
        ? "/product-master/import/template.xlsx"
        : "/product-master/import/template";
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "xlsx" ? "product-master-template.xlsx" : "product-master-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Template download failed"); }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/product-master/import?dry_run=${dryRun}`,
        fd, { headers: { "Content-Type": "multipart/form-data" }});
      setResult(res.data);
      if (res.data.errors === 0 && !dryRun) toast.success(`Committed ${res.data.committed} products`);
      else if (dryRun) toast.info(`Dry run: ${res.data.parsed} parsed, ${res.data.errors} errors`);
      else toast.error(`Import blocked — ${res.data.errors} error(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="import-panel">
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-[13px] font-semibold text-slate-800 mb-2 flex items-center gap-2">
          <Upload className="h-4 w-4 text-emerald-700" /> Bulk import products (Excel / CSV)
        </div>
        <p className="text-[11px] text-slate-500 mb-3">
          Download the Excel template (it has an <b>Instructions</b> sheet explaining every column and two
          example rows). Fill your products, delete the example rows, then upload here. Only
          <b> product_name</b> is required. Dry-run first — a commit only proceeds when the entire file is
          error-free.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => downloadTemplate("xlsx")}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="import-template-xlsx-btn">
            <Download className="h-3 w-3 mr-1" /> Download Excel template
          </Button>
          <Button size="sm" variant="outline" onClick={() => downloadTemplate("csv")}
            data-testid="import-template-btn">
            <Download className="h-3 w-3 mr-1" /> CSV template
          </Button>
          <input type="file" accept=".xlsx,.xlsm,.csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-[12px]" data-testid="import-file" />
          <label className="flex items-center gap-2 text-[11px] text-slate-600">
            <Switch checked={dryRun} onCheckedChange={setDryRun}
              data-testid="import-dry-run-switch" />
            Dry run (do not commit)
          </label>
          <Button size="sm" onClick={upload} disabled={!file || busy}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="import-upload">
            <Upload className="h-3 w-3 mr-1" /> {busy ? "Uploading…" : "Upload"}
          </Button>
        </div>
      </div>
      {result && (
        <div className="bg-white border border-slate-200 rounded-lg p-4"
          data-testid="import-result">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <Stat label="Parsed"      value={result.parsed} tone="ok" />
            <Stat label="Errors"      value={result.errors}
              tone={result.errors > 0 ? "err" : "ok"} />
            <Stat label="Committed" value={result.committed} />
            <Stat label="Mode"         value={result.dry_run ? "Dry run" : "Commit"} />
          </div>
          {(result.sample_errors || []).length > 0 && (
            <div className="border border-rose-200 bg-rose-50 rounded p-2">
              <div className="text-[11px] font-semibold text-rose-800 mb-1">Sample errors</div>
              <div className="text-[11px] font-mono text-rose-900 max-h-48 overflow-y-auto">
                {result.sample_errors.map((e, i) =>
                  <div key={i}>Row {e.row}: {e.error}</div>)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ─── Data Quality ─────────────────────────────────────────────── */
function DataQualityPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get("/product-master/data-quality"); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="quality-panel">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-slate-500">
          Generated {data?.generated_at || "…"}
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy}
          data-testid="quality-refresh">
          <RefreshCw className={"h-3 w-3 mr-1 " + (busy ? "animate-spin" : "")} /> Refresh
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-[13px] font-semibold text-slate-800 mb-3">
          Products missing critical fields
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Object.entries(data?.missing_counts || {}).map(([f, c]) => (
            <div key={f} className={"border rounded p-2 "
              + (c > 0 ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200")}>
              <div className="text-[10px] uppercase text-slate-600">{f}</div>
              <div className="text-lg font-heading font-semibold">{c}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-[13px] font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-700" /> Duplicate suspects
        </div>
        {Object.entries(data?.duplicate_suspects || {}).map(([key, list]) => (
          <div key={key} className="mb-3">
            <div className="text-[11px] font-semibold text-slate-700 mb-1">{key}</div>
            {list.length === 0 ? (
              <div className="text-[11px] text-emerald-700">No duplicates</div>
            ) : (
              <table className="text-[11px] min-w-full border border-slate-200 rounded">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                  <tr><th className="p-2 text-left">Value</th>
                    <th className="p-2 text-left">Count</th>
                    <th className="p-2 text-left">Product IDs</th></tr>
                </thead>
                <tbody>
                  {list.map((d, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-2 font-mono">{d.value}</td>
                      <td className="p-2 text-rose-700 font-semibold">{d.count}</td>
                      <td className="p-2 font-mono text-slate-500">{d.product_ids.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function Stat({ label, value, tone }) {
  const tint = tone === "ok"   ? "text-emerald-800 bg-emerald-50 border-emerald-200"
             : tone === "warn" ? "text-amber-800 bg-amber-50 border-amber-200"
             : tone === "err"  ? "text-rose-800 bg-rose-50 border-rose-200"
             : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className={"border rounded p-2.5 " + tint}>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
      <div className="text-[18px] font-heading font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}


/* ============================================================
 *  ProductTechDocs — embedded Tech Library slice inside Product
 *  360.  Reuses the /tech-library API and the shared UploadModal.
 * ============================================================ */
function ProductTechDocs({ product }) {
  const [docs, setDocs] = useState([]);
  const [showUpload, setShowUpload] = useState(false);

  const load = useCallback(async () => {
    if (!product?.id) return;
    try {
      const { data } = await api.get(
        `/tech-library/products/${product.id}/documents`);
      setDocs(data.items || []);
    } catch (e) { /* silent */ }
  }, [product?.id]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mt-4"
      data-testid="p360-tech-docs">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            Technical Documents ({docs.length})
          </div>
          <div className="text-[11px] text-slate-500">
            Datasheets, brochures, manuals and certificates linked to this product.
          </div>
        </div>
        <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white"
          onClick={() => setShowUpload(true)}
          data-testid="p360-add-doc">
          <Upload className="h-3 w-3 mr-1" /> Add tech document
        </Button>
      </div>

      {docs.length === 0 ? (
        <div className="text-[12px] text-slate-500 italic p-2">
          No technical documents yet.  Add a datasheet to start.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {docs.map((d) => (
            <li key={d.id}
              data-testid={`p360-doc-${d.id}`}
              className="py-2 flex items-center gap-3">
              <FileText className="h-4 w-4 text-emerald-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-slate-900 truncate">
                  {d.title}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {(TECH_DOC_TYPES.find(([v]) => v === d.doc_type) || [null, d.doc_type])[1]}
                  {" · v"}{d.version}
                  {" · "}{d.filename}
                </div>
              </div>
              <span className={
                "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border font-medium " +
                (d.visibility === "CUSTOMER_SAFE"
                  ? "text-emerald-700 border-emerald-200 bg-emerald-50"
                  : "text-slate-700 border-slate-200 bg-slate-50")
              }>
                {d.visibility === "CUSTOMER_SAFE" ? (
                  <ShieldCheck className="h-3 w-3" />
                ) : <Lock className="h-3 w-3" />}
                {d.visibility === "CUSTOMER_SAFE" ? "customer safe" : "internal"}
              </span>
              <a href={`${api.defaults?.baseURL || ""}/tech-library/documents/${d.id}/file`}
                download
                data-testid={`p360-doc-dl-${d.id}`}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Download">
                <Download className="h-3.5 w-3.5" />
              </a>
            </li>
          ))}
        </ul>
      )}

      {showUpload && (
        <UploadModal preselectProduct={product}
          onClose={() => setShowUpload(false)}
          onSaved={() => { setShowUpload(false); load(); }} />
      )}
    </div>
  );
}


/* ============================================================
 *  ProductContent — enrichment status card inside Product 360.
 *  Wraps /api/product-content endpoints; every action is manual.
 * ============================================================ */
function ProductContent({ product }) {
  const [state, setState] = useState(null);
  const [deltas, setDeltas] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!product?.id) return;
    try {
      const { data } = await api.get(`/product-content/${product.id}`);
      setState(data);
    } catch { /* silent */ }
  }, [product?.id]);
  useEffect(() => { load(); }, [load]);

  const preview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/product-content/enrich/${product.id}`);
      setDeltas(data);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/product-content/enrich/${product.id}?apply=true`);
      toast.success(`Applied ${data.applied.fields_applied} field(s); `
                              + `${data.applied.documents_created.length} document(s) added.`);
      setDeltas(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const q = state?.quality || "UNKNOWN";
  const qClass = q === "COMPLETE"      ? "text-emerald-800 bg-emerald-50 border-emerald-200"
                       : q === "IMAGE_MISSING"     ? "text-slate-700 bg-slate-50 border-slate-200"
                       : q === "NO_PROVIDER_MATCH" ? "text-rose-700 bg-rose-50 border-rose-200"
                       :                                                        "text-amber-800 bg-amber-50 border-amber-200";

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mt-4"
      data-testid="p360-product-content">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
            ONE Product Content
          </div>
          <div className="text-[11px] text-slate-500">
            Automatic enrichment from configured OEM / catalogue providers.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={busy}
            onClick={preview} data-testid="pc-refresh">
            <RefreshCw className={"h-3 w-3 mr-1 " + (busy ? "animate-spin" : "")} />
            Refresh content
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <PcTile label="Quality" value={q} className={qClass}
          testId="pc-quality" />
        <PcTile label="Match"   value={state?.match_status || "—"}
          testId="pc-match" />
        <PcTile label="Provider" value={state?.provider || "—"} />
        <PcTile label="Documents" value={state?.documents_count ?? 0}
          testId="pc-docs-count" />
      </div>

      {state?.documents_needing_review > 0 && (
        <div className="text-[11px] p-2 rounded bg-amber-50 border border-amber-200 text-amber-800 mb-3">
          {state.documents_needing_review} document{state.documents_needing_review > 1 ? "s" : ""} awaiting review.
          Approve each below to make them customer-safe.
        </div>
      )}

      {deltas && (deltas.field_deltas?.length > 0 || deltas.documents?.length > 0) && (
        <div className="border border-emerald-200 rounded p-3 space-y-2 mb-3 bg-emerald-50/40"
          data-testid="pc-review">
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">
            Proposed changes ({deltas.provider})
          </div>
          {deltas.field_deltas?.length > 0 && (
            <div>
              <div className="text-[10px] text-slate-500 uppercase mb-1">Fields</div>
              {deltas.field_deltas.map((f) => (
                <div key={f.field} className="text-[11px] py-0.5 border-b border-emerald-100 last:border-0">
                  <span className="font-mono">{f.field}</span>:{" "}
                  <span className="line-through text-slate-500">{String(f.current || "—")}</span>
                  {" → "}
                  <span className="text-emerald-800 font-medium">{String(f.proposed)}</span>
                </div>
              ))}
              <div className="text-[10px] text-slate-500 italic mt-1">
                Only empty fields will be filled; existing values stay untouched.
              </div>
            </div>
          )}
          {deltas.document_actions?.length > 0 && (
            <div>
              <div className="text-[10px] text-slate-500 uppercase mt-2 mb-1">Documents</div>
              {deltas.document_actions.map((a, i) => (
                <div key={i} className="text-[11px] py-0.5">
                  <span className="font-mono">{a.action}</span> — {a.title}
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={apply} disabled={busy}
              data-testid="pc-apply"
              className="bg-emerald-700 hover:bg-emerald-800 text-white">
              Apply changes
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDeltas(null)}>
              Discard
            </Button>
          </div>
        </div>
      )}

      <div className="text-[10px] text-slate-500">
        Last checked {state?.last_checked
          ? new Date(state.last_checked).toLocaleString()
          : "never"}
        {state?.last_applied_at && (
          <> · Last applied {new Date(state.last_applied_at).toLocaleString()}</>
        )}
      </div>
    </div>
  );
}

function PcTile({ label, value, className, testId }) {
  return (
    <div data-testid={testId}
      className={"border rounded p-2 " + (className || "border-slate-200 bg-slate-50")}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-[13px] font-semibold mt-0.5 font-mono truncate">
        {String(value)}
      </div>
    </div>
  );
}
